import {
  Box,
  Typography,
  List,
  ListItemText,
  ListItemButton,
  IconButton,
  Chip,
  Paper,
  Button,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { slugify } from '../../utils/slugify';
import {
  PlayArrow,
  Delete,
  QueueMusic,
  Refresh,
  PlaylistAdd,
  Link,
  Share,
} from '@mui/icons-material';
import { useState, memo, useCallback, useRef, useEffect } from 'react';
import { usePlayerStore } from '../../stores/playerStore';
import { useSettingsStore } from '../../stores/settingsStore';
import type { MediaItem } from '../../models/types';

const QueueItem = memo(({
  item,
  index,
  isCurrent,
  onPlay,
  onRemove,
  isAutoFetched
}: {
  item: MediaItem;
  index: number;
  isCurrent: boolean;
  onPlay: (index: number) => void;
  onRemove: (index: number) => void;
  isAutoFetched?: boolean;
}) => {
  const handlePlay = useCallback(() => {
    onPlay(index);
  }, [onPlay, index]);

  const handleRemove = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onRemove(index);
  }, [onRemove, index]);

  const handleViewSource = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (item.genre) {
      window.open(item.genre, '_blank');
    }
  }, [item.genre]);

  const handleShare = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    const collection = item.extras?.collection || item.artist || '';
    const songId = item.extras?.songId || item.extras?.song_id || '';
    const showId = item.extras?.showId || item.extras?.show_id || '';
    const source = item.extras?.source || 'ls';

    // Get current mode settings
    const { fullShowMode, continueJamsMode } = useSettingsStore.getState();
    let mode = 'normal';
    if (fullShowMode) {
      mode = 'fullShow';
    } else if (continueJamsMode) {
      mode = 'continueJams';
    }

    const slug = slugify(item.title || '');
    const url = new URL('https://app.liveshuffle.net');
    url.searchParams.set('share', 'true');
    url.searchParams.set('collection', collection);
    url.searchParams.set('title', slug);
    url.searchParams.set('date', item.album || '');
    url.searchParams.set('songId', songId);
    url.searchParams.set('showId', showId);
    url.searchParams.set('mode', mode);
    url.searchParams.set('source', source);

    try {
      // Only use native share on mobile devices - desktop share sheets often lack copy option
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (navigator.share && isMobile) {
        await navigator.share({
          title: `${item.title} - ${item.artist}`,
          url: url.toString(),
        });
      } else {
        await navigator.clipboard.writeText(url.toString());
      }
    } catch (err) {
      console.log('Share cancelled or failed:', err);
    }
  }, [item]);

  const getTrackNumber = () => {
    const track = item.extras?.track as string;
    return track ? `Track ${track}` : '';
  };

  return (
    <ListItemButton
      selected={isCurrent}
      onClick={handlePlay}
      sx={{
        borderRadius: 2,
        mb: 0.5,
        py: 1,
        px: 2,
        bgcolor: isCurrent ? 'rgba(66, 165, 245, 0.16)' : 'background.paper',
        border: '1px solid',
        borderColor: isCurrent ? 'primary.main' : 'transparent',
        '&:hover': {
          bgcolor: isCurrent ? 'rgba(66, 165, 245, 0.20)' : 'action.hover',
        },
        transition: 'all 0.15s ease-in-out',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
        {isCurrent && (
          <PlayArrow 
            fontSize="small" 
            sx={{ color: 'primary.main' }} 
          />
        )}
        
        <ListItemText
          sx={{ flex: 1, minWidth: 0 }}
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
              <Typography
                variant="body2"
                noWrap
                sx={{
                  flex: 1,
                  color: isCurrent ? 'primary.main' : 'text.primary',
                  fontWeight: isCurrent ? 500 : 400,
                }}
              >
                {item.title}
                {isAutoFetched && (
                  <Typography 
                    component="span" 
                    sx={{ 
                      color: 'text.secondary',
                      fontSize: '0.75rem',
                      ml: 0.5,
                    }}
                  >
                    (auto)
                  </Typography>
                )}
              </Typography>
              {getTrackNumber() && (
                <Chip
                  label={getTrackNumber()}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: '0.65rem',
                    height: 18,
                    borderColor: isCurrent ? 'primary.main' : 'divider',
                    color: isCurrent ? 'primary.main' : 'text.secondary',
                  }}
                />
              )}
            </Box>
          }
          secondary={
            <Typography
              variant="caption"
              noWrap
              sx={{
                color: isCurrent ? 'primary.dark' : 'text.secondary',
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {item.artist} • {item.album}
            </Typography>
          }
        />

        <Tooltip title="View source">
          <span>
            <IconButton
              edge="start"
              size="small"
              onClick={handleViewSource}
              disabled={!item.genre}
              sx={{
                color: 'text.secondary',
                opacity: 0.6,
                '&:hover': {
                  color: 'primary.main',
                  opacity: 1,
                }
              }}
            >
              <Link fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Share">
          <span>
            <IconButton
              size="small"
              onClick={handleShare}
              sx={{
                color: 'text.secondary',
                opacity: 0.6,
                '&:hover': {
                  color: 'primary.main',
                  opacity: 1,
                }
              }}
            >
              <Share fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Remove from queue">
          <span>
            <IconButton
              edge="end"
              size="small"
              onClick={handleRemove}
              sx={{
                color: 'text.secondary',
                opacity: 0.6,
                '&:hover': {
                  color: 'error.main',
                  opacity: 1,
                }
              }}
            >
              <Delete fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </ListItemButton>
  );
});

QueueItem.displayName = 'QueueItem';

const EnhancedQueue = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  
  const {
    queue,
    currentIndex,
    playItemAt,
    removeItemAt,
    addFullShowAfterCurrentSong,
    loadFullShowFromBeginning,
    clearUpcomingSongs,
  } = usePlayerStore();
  const listRef = useRef<HTMLDivElement>(null);
  const currentItemRef = useRef<HTMLDivElement>(null);
  const [autoFetchedCount] = useState(0);

  // Auto-scroll to current song when it changes
  useEffect(() => {
    if (currentItemRef.current) {
      const itemElement = currentItemRef.current;
      setTimeout(() => {
        itemElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 100);
    }
  }, [currentIndex]);

  const handlePlayItem = useCallback((index: number) => {
    playItemAt(index);
  }, [playItemAt]);

  const handleRemoveItem = useCallback((index: number) => {
    removeItemAt(index);
  }, [removeItemAt]);

  const handleClearUpcoming = useCallback(async () => {
    await clearUpcomingSongs();
  }, [clearUpcomingSongs]);

  const handleAddRestOfShow = useCallback(async () => {
    const currentItem = queue[currentIndex];
    if (!currentItem) return;
    const collection = currentItem.extras?.collection || currentItem.artist || '';
    const showId = currentItem.extras?.show_id || currentItem.extras?.showId;
    const track = currentItem.extras?.track;
    const source = currentItem.extras?.source || 'ls';
    if (showId && track) {
      await addFullShowAfterCurrentSong(collection, showId, track, source);
    }
  }, [queue, currentIndex, addFullShowAfterCurrentSong]);

  const handleLoadFullShow = useCallback(async () => {
    const currentItem = queue[currentIndex];
    if (!currentItem) return;
    const collection = currentItem.extras?.collection || currentItem.artist || '';
    const showId = currentItem.extras?.show_id || currentItem.extras?.showId;
    const source = currentItem.extras?.source || 'ls';
    if (showId) {
      await loadFullShowFromBeginning(collection, showId, source);
    }
  }, [queue, currentIndex, loadFullShowFromBeginning]);

  if (queue.length === 0) {
    return (
      <Paper 
        elevation={1}
        sx={{ 
          p: 3, 
          textAlign: 'center',
          borderRadius: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <QueueMusic sx={{ fontSize: 48, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No songs in queue
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Select some bands to start building your queue
        </Typography>
      </Paper>
    );
  }

  const songsRemaining = queue.length - currentIndex - 1;
  const autoFetchedStartIndex = queue.length - autoFetchedCount;

  return (
    <Paper 
      elevation={1}
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box sx={{
        p: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, gap: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.5 }}>
              Queue
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {queue.length} {queue.length === 1 ? 'song' : 'songs'}
            </Typography>
          </Box>
          <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            flexWrap: 'wrap', 
            justifyContent: 'flex-end',
            maxWidth: isSmallScreen ? '200px' : 'none',
          }}>
            <Tooltip title="Add remaining songs from current show">
              <Button
                variant="outlined"
                startIcon={!isSmallScreen && <PlaylistAdd />}
                onClick={handleAddRestOfShow}
                size="small"
                disabled={currentIndex < 0}
                sx={{ 
                  textTransform: 'none',
                  minWidth: isSmallScreen ? '40px' : 'auto',
                  px: isSmallScreen ? 1 : 2,
                }}
              >
                {isSmallScreen ? <PlaylistAdd /> : 'Rest of Show'}
              </Button>
            </Tooltip>
            <Tooltip title="Load full show from beginning">
              <Button
                variant="outlined"
                startIcon={!isSmallScreen && <QueueMusic />}
                onClick={handleLoadFullShow}
                size="small"
                disabled={currentIndex < 0}
                sx={{ 
                  textTransform: 'none',
                  minWidth: isSmallScreen ? '40px' : 'auto',
                  px: isSmallScreen ? 1 : 2,
                }}
              >
                {isSmallScreen ? <QueueMusic /> : 'Full Show'}
              </Button>
            </Tooltip>
            <Tooltip title="Clear upcoming and refresh queue">
              <Button
                variant="outlined"
                startIcon={!isSmallScreen && <Refresh />}
                onClick={handleClearUpcoming}
                size="small"
                sx={{ 
                  textTransform: 'none',
                  minWidth: isSmallScreen ? '40px' : 'auto',
                  px: isSmallScreen ? 1 : 2,
                }}
              >
                {isSmallScreen ? <Refresh /> : 'Refresh'}
              </Button>
            </Tooltip>
          </Box>
        </Box>
        
        {songsRemaining <= 3 && (
          <Box sx={{ 
            p: 1.5, 
            bgcolor: songsRemaining === 0 ? 'error.light' : 'warning.light',
            borderRadius: 1,
            border: '1px solid',
            borderColor: songsRemaining === 0 ? 'error.main' : 'warning.main',
          }}>
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 500, 
                color: songsRemaining === 0 ? 'error.dark' : 'warning.dark' 
              }}
            >
              {songsRemaining === 0 ? 'No more songs!' : `Only ${songsRemaining} song${songsRemaining !== 1 ? 's' : ''} remaining`}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Queue List */}
      <Box 
        ref={listRef}
        sx={{ 
          flex: 1, 
          overflow: 'auto',
          p: 1,
          bgcolor: 'background.default',
        }}
      >
        <List sx={{ py: 0 }}>
          {queue.map((item: MediaItem, index: number) => {
            const isCurrent = index === currentIndex;
            const isAutoFetched = index >= autoFetchedStartIndex;
            
            return (
              <div key={`${item.id}-${index}`} ref={isCurrent ? currentItemRef : undefined}>
                <QueueItem
                  item={item}
                  index={index}
                  isCurrent={isCurrent}
                  isAutoFetched={isAutoFetched}
                  onPlay={handlePlayItem}
                  onRemove={handleRemoveItem}
                />
              </div>
            );
          })}
        </List>
        
        {/* Load More indicator */}
        {songsRemaining <= 3 && (
          <Box sx={{
            p: 2,
            textAlign: 'center',
            bgcolor: 'primary.light',
            borderRadius: 2,
            mt: 2,
            mx: 1,
          }}>
            <Typography variant="body2" color="primary.dark" sx={{ mb: 0.5, fontWeight: 500 }}>
              Auto-fetching more songs...
            </Typography>
            <Typography variant="caption" color="primary.main">
              Based on your selected bands
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default memo(EnhancedQueue);
